/**
 * Query-term highlighting — pure, framework-free core.
 *
 * Splits a string into matched / unmatched segments so a renderer can wrap the
 * matched parts. Two matching modes:
 *
 * - default (typeahead): the query is tokenised into words and each word is
 *   substring-matched, so typing "appl max" highlights "Appl" and "Max".
 * - `exact` (product-name mentions): the whole query is matched as ONE literal
 *   phrase, so "Apple AirPods Max (2nd gen)" highlights only that exact run and
 *   never bleeds token-by-token into a neighbouring name ("Apple AirPods Pro").
 *
 * Design notes:
 * - Matching is `indexOf`-based, NOT `RegExp` — the query is user/content input,
 *   so a regex path would need escaping and could still throw on a stray `(`/`\`.
 * - Case-insensitivity uses `toLowerCase()` only (length-preserving), so match
 *   indices stay aligned with the original string. We deliberately do NOT
 *   accent-fold (NFD) because decomposition changes string length and would
 *   desync indices; the `lower.length === text.length` guard is cheap insurance
 *   against the rare locale lowercasing that does change length.
 */

export interface HighlightSegment {
  text: string
  /** True when this slice matched the query and should be emphasised. */
  match: boolean
}

interface Range {
  start: number
  end: number
}

const EDGE_PUNCT = /^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu

const SINGLE_WORD_CHAR = /^[\p{L}\p{N}]$/u

/**
 * Split a query into the words we highlight (typeahead mode): lowercased,
 * edge-punctuation trimmed, dropping 1-char *punctuation* noise so stray `(`, `&`
 * fragments don't paint half the string. Single alphanumerics ("AirPods 4",
 * "Beoplay H9") survive — `matchRanges` confines them to whole-word matches so a
 * lone "a" can't substring-paint every A. Internal punctuation (`wh-1000xm5`) is kept.
 */
export function toTerms(raw: string): string[] {
  return raw
    .toLowerCase()
    .split(/\s+/)
    .map(t => t.replace(EDGE_PUNCT, ''))
    .filter(t => t.length >= 2 || SINGLE_WORD_CHAR.test(t))
}

/** All match ranges for any term, merged so overlapping/touching hits render as
 *  one segment (no seam). Operates on already-lowercased text + terms.
 *  1-char terms match whole words only (see `toTerms`). */
function matchRanges(lowerText: string, terms: string[]): Range[] {
  const isWordChar = (c: string | undefined): boolean => c !== undefined && SINGLE_WORD_CHAR.test(c)
  const ranges: Range[] = []
  for (const term of terms) {
    if (!term) {
      continue
    }
    const wholeWordOnly = term.length === 1
    let from = 0
    for (;;) {
      const i = lowerText.indexOf(term, from)
      if (i === -1) {
        break
      }
      const end = i + term.length
      if (!wholeWordOnly || (!isWordChar(lowerText[i - 1]) && !isWordChar(lowerText[end]))) {
        ranges.push({ start: i, end })
      }
      from = end
    }
  }
  if (ranges.length === 0) {
    return ranges
  }
  ranges.sort((a, b) => a.start - b.start || a.end - b.end)
  const merged: Range[] = [{ ...ranges[0] }]
  for (let k = 1; k < ranges.length; k++) {
    const last = merged[merged.length - 1]
    // `<=` merges touching ranges too, collapsing e.g. "air"+"pods" into one span.
    if (ranges[k].start <= last.end) {
      last.end = Math.max(last.end, ranges[k].end)
    } else {
      merged.push({ ...ranges[k] })
    }
  }
  return merged
}

/**
 * Break `text` into matched/unmatched segments against `query`. Returns a single
 * unmatched segment when there's nothing to highlight, so a renderer can map the
 * result uniformly. Pass `{ exact: true }` to match the whole query as one phrase.
 */
export function splitHighlight(
  text: string,
  query: string | null | undefined,
  opts: { exact?: boolean } = {}
): HighlightSegment[] {
  if (!text) {
    return []
  }

  let terms: string[]
  if (!query) {
    terms = []
  } else if (opts.exact) {
    const phrase = query.trim().toLowerCase()
    terms = phrase ? [phrase] : []
  } else {
    terms = toTerms(query)
  }
  if (terms.length === 0) {
    return [{ text, match: false }]
  }

  const lower = text.toLowerCase()
  if (lower.length !== text.length) {
    // Lowercasing changed length (rare locale case) → indices would desync.
    return [{ text, match: false }]
  }

  const ranges = matchRanges(lower, terms)
  if (ranges.length === 0) {
    return [{ text, match: false }]
  }

  const segments: HighlightSegment[] = []
  let cursor = 0
  for (const r of ranges) {
    if (r.start > cursor) {
      segments.push({ text: text.slice(cursor, r.start), match: false })
    }
    segments.push({ text: text.slice(r.start, r.end), match: true })
    cursor = r.end
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), match: false })
  }
  return segments
}
